import { useCallback, useRef, useState } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { APPROVALS_ANALYTICS_SOURCE } from '@confluence/approvals/entry-points/analytics.ts';
import { useLiveDocConversionBlockedModal } from '@confluence/approvals/entry-points/useLiveDocConversionBlockedModal.ts';
import {
	type ApprovalReviewStatus,
	usePreventFirstPublishWithoutApproval,
} from '@confluence/editor-features/entry-points/usePreventFirstPublishWithoutApproval.ts';

type ApprovalPublishBlockedModal = 'firstPublish' | 'liveDocConversion' | null;

type UseApprovalPublishBlockedModalArgs = {
	approvalRequestLoading: boolean;
	approvalReviewStatus: string | null | undefined;
	contentId: string;
	contentType: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	disallowFirstPublish: boolean | undefined;
	isLivePage: boolean;
	isUnpublishedDraft: boolean;
	spaceKey: string;
};

const isApprovalReviewStatus = (
	reviewStatus: string | null | undefined,
): reviewStatus is ApprovalReviewStatus =>
	reviewStatus === 'APPROVED' ||
	reviewStatus === 'CANCELED' ||
	reviewStatus === 'DENIED' ||
	reviewStatus === 'PENDING';

export const useApprovalPublishBlockers = ({
	approvalRequestLoading,
	approvalReviewStatus,
	contentId,
	contentType,
	createAnalyticsEvent,
	disallowFirstPublish,
	isLivePage,
	isUnpublishedDraft,
	spaceKey,
}: UseApprovalPublishBlockedModalArgs): {
	approvalPublishBlockedModal: ApprovalPublishBlockedModal;
	handlePublishBlockedByApproval: () => void;
	handleCloseApprovalPublishBlockedModal: () => void;
	preventFirstPublishWithoutApproval: () => Promise<boolean>;
	shouldBlockLiveDocConversion: boolean;
} => {
	const [isFirstPublishBlockedModalOpen, setIsFirstPublishBlockedModalOpen] = useState(false);
	const isFirstPublishBlockedModalOpenRef = useRef(false);
	const {
		handleCloseLiveDocConversionBlockedModal,
		handleLiveDocConversionBlockedByApproval,
		isLiveDocConversionBlockedModalOpen,
		shouldBlockLiveDocConversion,
	} = useLiveDocConversionBlockedModal({
		contentId,
		contentType,
		createAnalyticsEvent,
		disallowFirstPublish,
		isLivePage,
	});

	const approvalPublishBlockedModal: ApprovalPublishBlockedModal =
		isLiveDocConversionBlockedModalOpen
			? 'liveDocConversion'
			: isFirstPublishBlockedModalOpen
				? 'firstPublish'
				: null;

	const handleCloseApprovalPublishBlockedModal = useCallback((): void => {
		if (approvalPublishBlockedModal === 'firstPublish') {
			isFirstPublishBlockedModalOpenRef.current = false;
			setIsFirstPublishBlockedModalOpen(false);
			return;
		}

		if (approvalPublishBlockedModal === 'liveDocConversion') {
			handleCloseLiveDocConversionBlockedModal();
		}
	}, [approvalPublishBlockedModal, handleCloseLiveDocConversionBlockedModal]);

	const handleFirstPublishBlockedByApproval = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'blocked',
				actionSubject: 'publish',
				actionSubjectId: 'blockedByApprovalPolicy',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: {
					contentId,
					contentType,
					isUnpublishedDraft,
				},
			},
		}).fire();

		if (!isFirstPublishBlockedModalOpenRef.current) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'shown',
					actionSubject: 'modal',
					actionSubjectId: 'approvalRequiredBeforePublishing',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: {
						contentId,
						contentType,
						isUnpublishedDraft,
					},
				},
			}).fire();
		}

		isFirstPublishBlockedModalOpenRef.current = true;
		setIsFirstPublishBlockedModalOpen(true);
	}, [contentId, contentType, createAnalyticsEvent, isUnpublishedDraft]);

	const handlePublishBlockedByApproval = useCallback((): void => {
		if (shouldBlockLiveDocConversion) {
			handleLiveDocConversionBlockedByApproval();
			return;
		}

		handleFirstPublishBlockedByApproval();
	}, [
		handleFirstPublishBlockedByApproval,
		handleLiveDocConversionBlockedByApproval,
		shouldBlockLiveDocConversion,
	]);

	const preventFirstPublishWithoutApproval = usePreventFirstPublishWithoutApproval({
		approvalRequestLoading,
		approvalReviewStatus: isApprovalReviewStatus(approvalReviewStatus)
			? approvalReviewStatus
			: undefined,
		contentId,
		disallowFirstPublish,
		isUnpublishedDraft,
		onPublishBlocked: handlePublishBlockedByApproval,
		shouldPreventPublish: shouldBlockLiveDocConversion,
		spaceKey,
	});

	return {
		approvalPublishBlockedModal,
		handlePublishBlockedByApproval,
		handleCloseApprovalPublishBlockedModal,
		preventFirstPublishWithoutApproval,
		shouldBlockLiveDocConversion,
	};
};
